'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/lib/context/AuthContext';
import { CheckCircle, AlertCircle, Clock, Lock, Award } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/Textarea';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useToast } from '@/lib/context/ToastContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queries/queryKeys';
import CertificateGenerator from '@/components/certificates/CertificateGenerator';
import { useServerNow } from '@/lib/hooks/useServerNow';

type SurveyStatus = 'open' | 'closed' | 'upcoming';

interface Survey {
  id: string;
  title: string;
  description: string | null;
  is_anonymous: boolean | null;
  is_required: boolean | null;
  available_from: string | null;
  available_until: string | null;
  available_from_anchor: 'absolute' | 'event_start' | 'event_end';
  available_until_anchor: 'absolute' | 'event_start' | 'event_end';
  status: SurveyStatus;
}

interface SurveyQuestion {
  id: string;
  question_text: string;
  type: 'multiple_choice' | 'single_choice' | 'true_false' | 'essay';
  is_required: boolean | null;
  options?: SurveyQuestionOption[];
}

interface SurveyQuestionOption {
  id: string;
  option_text: string;
}

interface SurveyTakingProps {
  eventId: string;
  enrollmentId: string;
}

export default function SurveyTaking({ eventId, enrollmentId }: SurveyTakingProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [surveyStarted, setSurveyStarted] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [certificateCreated, setCertificateCreated] = useState(false);
  const [certificateContentId, setCertificateContentId] = useState<string | null>(null);
  const [certificateContentTitle, setCertificateContentTitle] = useState<string | null>(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const supabase = createClient();
  const serverNow = useServerNow();

  // Fetch parent event start/end so anchored survey windows resolve correctly
  const { data: eventTimes } = useQuery({
    queryKey: ['event', eventId, 'times'],
    queryFn: async () => {
      const { data } = await supabase
        .from('content')
        .select('start_date, end_date')
        .eq('id', eventId)
        .single();
      return {
        start_date: data?.start_date ?? null,
        end_date: data?.end_date ?? null,
      };
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!eventId,
  });

  const getSurveyStatus = (survey: {
    available_from: string | null;
    available_until: string | null;
    available_from_anchor?: 'absolute' | 'event_start' | 'event_end' | null;
    available_until_anchor?: 'absolute' | 'event_start' | 'event_end' | null;
  }): SurveyStatus => {
    const eventInfo = eventTimes ?? { start_date: null, end_date: null };
    const fromAnchor = survey.available_from_anchor ?? 'absolute';
    const untilAnchor = survey.available_until_anchor ?? 'absolute';
    const fromTs =
      fromAnchor === 'event_start' ? eventInfo.start_date :
      fromAnchor === 'event_end' ? eventInfo.end_date :
      survey.available_from;
    const untilTs =
      untilAnchor === 'event_start' ? eventInfo.start_date :
      untilAnchor === 'event_end' ? eventInfo.end_date :
      survey.available_until;
    if (fromTs && new Date(fromTs) > serverNow) return 'upcoming';
    if (untilTs && new Date(untilTs) < serverNow) return 'closed';
    return 'open';
  };

  // Fetch surveys list via TanStack Query
  const { data: rawSurveys = [], isLoading: surveysLoading } = useQuery<Omit<Survey, 'status'>[]>({
    queryKey: queryKeys.events.surveys(eventId),
    queryFn: async () => {
      // Only surveys a participant can meaningfully answer: enabled, and with at
      // least one question. A question-less survey used to render, then mark
      // itself complete on open - satisfying certificate requirements without a
      // single answer being given.
      const { data, error } = await supabase
        .from('surveys')
        .select('*, survey_questions(count)')
        .eq('content_id', eventId)
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return (data || []).filter(
        (survey) => ((survey as { survey_questions?: { count: number }[] }).survey_questions?.[0]?.count ?? 0) > 0
      );
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!eventId,
  });

  // Derive surveys with current status (time-dependent)
  const surveys = rawSurveys
    .map(survey => ({ ...survey, status: getSurveyStatus(survey) }))
    .sort((a, b) => {
      const order = { open: 0, closed: 1, upcoming: 2 };
      return order[a.status] - order[b.status];
    });

  // Fetch completed surveys via TanStack Query
  const { data: completedSurveyIds = [], isLoading: completionsLoading } = useQuery<string[]>({
    queryKey: queryKeys.events.userSurveyCompletions(eventId, enrollmentId),
    queryFn: async () => {
      const { data: responses } = await supabase
        .from('survey_responses')
        .select('survey_id')
        .eq('user_id', user!.id)
        .eq('enrollment_id', enrollmentId);

      return (responses || []).map(r => r.survey_id);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!eventId && !!enrollmentId && !!user?.id,
  });
  const completedSurveys = new Set(completedSurveyIds);

  const loading = surveysLoading || (!!user?.id && !!enrollmentId && completionsLoading);

  const fetchQuestions = useCallback(async (surveyId: string) => {
    // Check TanStack Query cache first
    const cached = queryClient.getQueryData<SurveyQuestion[]>(queryKeys.events.surveyQuestions(surveyId));
    if (cached) {
      setQuestions(cached);
      return;
    }

    const { data, error } = await supabase
      .from('survey_questions')
      .select(`
        *,
        options:survey_question_options(*)
      `)
      .eq('survey_id', surveyId)
      .order('sort_order');

    if (!error && data) {
      queryClient.setQueryData(queryKeys.events.surveyQuestions(surveyId), data);
      setQuestions(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  const startSurvey = async (survey: Survey) => {
    // Check if already completed
    if (completedSurveys.has(survey.id)) {
      toast.info(t('event.survey.alreadyCompleted'));
      return;
    }

    // Re-check availability before starting (in case status changed)
    const currentStatus = getSurveyStatus(survey);
    if (currentStatus === 'upcoming') {
      toast.warning(t('event.survey.notYetAvailable'));
      return;
    }
    if (currentStatus === 'closed') {
      toast.warning(t('event.survey.missedSurvey'));
      return;
    }

    setSelectedSurvey(survey);
    await fetchQuestions(survey.id);
    setSurveyStarted(true);
    setAnswers({});
    setSurveyCompleted(false);
  };

  const handleAnswer = (questionId: string, answer: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const validateAnswers = (): boolean => {
    // Check if all required questions are answered
    const unansweredRequired = questions.filter(q => {
      if (!q.is_required) return false;
      const answer = answers[q.id];
      return !answer || (Array.isArray(answer) && answer.length === 0);
    });

    if (unansweredRequired.length > 0) {
      toast.warning(t('event.survey.answerRequired'));
      return false;
    }

    return true;
  };

  const submitSurvey = async () => {
    if (!selectedSurvey || !user) return;

    if (!validateAnswers()) return;

    // Save survey response through the server: it checks the enrollment and
    // writes the row (survey_responses accepts no client writes).
    let ok = false;
    try {
      const res = await fetch(`/api/surveys/${selectedSurvey.id}/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enrollment_id: enrollmentId, answers }),
      });
      if (res.status === 409) {
        const data = await res.json().catch(() => null);
        if (data?.errorCode === 'ALREADY_SUBMITTED') {
          queryClient.invalidateQueries({ queryKey: queryKeys.events.userSurveyCompletions(eventId, enrollmentId) });
          setSurveyStarted(false);
          toast.info(t('event.survey.alreadyCompleted'));
          return;
        }
      }
      ok = res.ok;
    } catch {
      ok = false;
    }

    if (ok) {
      // Milestone creation is handled by DB trigger on survey_responses (survey_completed).

      // Invalidate user progress queries so next fetch gets fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.events.userSurveyCompletions(eventId, enrollmentId) });

      setSurveyCompleted(true);
      setSurveyStarted(false);

      // Certificate auto-generation is handled atomically by a DB trigger on
      // enrollment_milestones (survey_completed). Check if cert was created.
      if (selectedSurvey.is_required) {
        try {
          const { data: cert } = await supabase
            .from('certifications')
            .select('id, content:content_id(title)')
            .eq('user_id', user?.id)
            .eq('content_id', eventId)
            .maybeSingle();
          if (cert) {
            setCertificateCreated(true);
            setCertificateContentId(eventId);
            setCertificateContentTitle((cert.content as { title?: string })?.title || '');
          } else {
            // Trigger failed – retry via auto-generate endpoint
            const retryRes = await fetch('/api/certificates/auto-generate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contentId: eventId }),
            });
            if (retryRes.ok) {
              const retryData = await retryRes.json();
              if (retryData.pendingCertCreated || retryData.alreadyExists) {
                setCertificateCreated(true);
                setCertificateContentId(eventId);
                setCertificateContentTitle(retryData.contentTitle || '');
              }
            }
          }
        } catch {
          // Don't fail the survey submission if cert check fails
        }
      }
    } else {
      toast.error(t('event.survey.errorSubmitting'));
    }
  };

  if (loading) {
    return <div className="text-center py-8">{t('event.survey.loading')}</div>;
  }

  if (surveys.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">{t('event.survey.noSurveysAvailable')}</p>
        <p className="text-sm text-gray-500 mt-2">{t('event.survey.checkBackLater')}</p>
      </div>
    );
  }

  if (surveyCompleted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-gray-300 rounded-lg p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('event.survey.thankYouTitle')}</h2>
          <p className="text-gray-600 mb-6">
            {t('event.survey.submittedSuccess')}
          </p>

          {certificateCreated && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-center gap-2">
                <Award className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-800">
                  {t('certificate.certificateEligible')}
                </span>
              </div>
            </div>
          )}

          <Button
            variant="default"
            onClick={() => {
              setSelectedSurvey(null);
              setSurveyCompleted(false);
              setCertificateCreated(false);
              queryClient.invalidateQueries({ queryKey: queryKeys.events.surveys(eventId) });
              queryClient.invalidateQueries({ queryKey: queryKeys.events.userSurveyCompletions(eventId, enrollmentId) });
            }}
          >
            {t('event.survey.backToSurveys')}
          </Button>
        </div>

        {showCertificateModal && certificateContentId && (
          <CertificateGenerator
            courseId={certificateContentId}
            courseName={certificateContentTitle || ''}
            onClose={() => setShowCertificateModal(false)}
          />
        )}
      </div>
    );
  }

  if (surveyStarted && selectedSurvey) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold">{selectedSurvey.title}</h2>
          {selectedSurvey.description && (
            <p className="text-gray-600 mt-2">{selectedSurvey.description}</p>
          )}
          {selectedSurvey.is_anonymous && (
            <p className="text-sm text-blue-600 mt-2">{t('surveyTaking.anonymousSurvey')}</p>
          )}
        </div>

        <div className="space-y-6">
          {questions.map((question, index) => (
            <div key={question.id} className="bg-white border border-gray-300 rounded-lg p-6">
              <h3 className="font-semibold mb-4">
                {t('surveyTaking.question')} {index + 1}
                {question.is_required && <span className="text-red-500">*</span>}
              </h3>
              <p className="text-gray-800 mb-4">{question.question_text}</p>

              {question.type === 'essay' ? (
                <Textarea
                  value={(answers[question.id] as string) || ''}
                  onChange={(e) => handleAnswer(question.id, e.target.value)}
                  rows={6}
                  placeholder={t('surveyTaking.answerPlaceholder')}
                />
              ) : question.type === 'multiple_choice' ? (
                <div className="space-y-2">
                  {question.options?.map((option) => (
                    <label key={option.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Array.isArray(answers[question.id]) && (answers[question.id] as string[]).includes(option.id)}
                        onChange={(e) => {
                          const current = Array.isArray(answers[question.id]) ? answers[question.id] as string[] : [];
                          const updated = e.target.checked
                            ? [...current, option.id]
                            : current.filter(id => id !== option.id);
                          handleAnswer(question.id, updated);
                        }}
                        className="h-4 w-4"
                      />
                      <span>{option.option_text}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {question.options?.map((option) => (
                    <label key={option.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        checked={answers[question.id] === option.id}
                        onChange={() => handleAnswer(question.id, option.id)}
                        className="h-4 w-4"
                      />
                      <span>{option.option_text}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end gap-4">
          <Button variant="secondary" onClick={() => setShowExitModal(true)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submitSurvey}>
            {t('event.survey.submitSurvey')}
          </Button>
        </div>

        {/* Exit Confirmation Modal */}
        <Modal
          isOpen={showExitModal}
          onClose={() => setShowExitModal(false)}
          title={t('event.confirmExit')}
          description={t('event.progressWillBeLost')}
          size="sm"
          showCloseButton={false}
        >
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowExitModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowExitModal(false);
                setSurveyStarted(false);
                setSelectedSurvey(null);
              }}
            >
              {t('event.exitAnyway')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // Categorize surveys by status
  const openSurveys = surveys.filter(s => s.status === 'open');
  const closedSurveys = surveys.filter(s => s.status === 'closed');
  const upcomingSurveys = surveys.filter(s => s.status === 'upcoming');

  const SurveyCard = ({ survey }: { survey: Survey }) => {
    const isCompleted = completedSurveys.has(survey.id);
    const isClosed = survey.status === 'closed';
    const isUpcoming = survey.status === 'upcoming';
    const isDisabled = isClosed || isUpcoming;

    return (
      <div
        className={`rounded-lg p-6 border transition-all ${
          isClosed
            ? 'bg-gray-50 border-gray-300 opacity-75'
            : isUpcoming
            ? 'bg-gray-50 border-gray-300 opacity-75'
            : survey.is_required
            ? 'bg-amber-50 border-amber-300'
            : 'bg-white border-gray-300 hover:border-blue-400'
        }`}
      >
        {/* Badge row: survey type + status (matching TestCard) */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Type badge */}
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${
              survey.is_required
                ? 'bg-amber-100 text-amber-800'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {survey.is_required ? t('courseDetails.required') : t('courseDetails.optional')}
            </span>

            {/* Status badge for closed/upcoming */}
            {isClosed && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-200 text-gray-600 flex items-center gap-1">
                <Lock className="h-3 w-3" />
                {t('event.survey.closed')}
              </span>
            )}
            {isUpcoming && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-200 text-yellow-700 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t('event.survey.upcoming')}
              </span>
            )}
            {isCompleted && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                {t('common.completed')}
              </span>
            )}
          </div>

          {/* Anonymous badge */}
          {survey.is_anonymous && !isCompleted && !isDisabled && (
            <span className="text-xs text-blue-600 font-semibold">
              {t('event.survey.anonymous')}
            </span>
          )}
        </div>

        <h3 className={`text-xl font-bold mb-2 ${isClosed ? 'text-gray-700' : ''}`}>
          {survey.title}
        </h3>
        {survey.description && (
          <p className={`mb-4 text-sm ${isClosed ? 'text-gray-500' : 'text-gray-600'}`}>
            {survey.description}
          </p>
        )}

        {/* Completion result for closed surveys that were completed */}
        {isClosed && isCompleted && (
          <div className="mb-4 p-3 rounded-lg border bg-green-50 border-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-green-700">
                {t('common.completed')}
              </span>
            </div>
          </div>
        )}

        {/* Action button */}
        {!isDisabled ? (
          isCompleted ? (
            <div className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg text-center font-semibold flex items-center justify-center gap-2">
              <CheckCircle className="h-4 w-4" />
              {t('common.completed')}
            </div>
          ) : (
            <Button
              onClick={() => startSurvey(survey)}
              className="w-full"
            >
              {t('event.survey.startSurvey')}
            </Button>
          )
        ) : isClosed && !isCompleted ? (
          <div className="text-center text-sm text-gray-500 py-2">
            {t('event.survey.missedSurvey')}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Open surveys */}
      {openSurveys.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">{t('event.survey.availableSurveysTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {openSurveys.map((survey) => (
              <SurveyCard key={survey.id} survey={survey} />
            ))}
          </div>
        </div>
      )}

      {/* Closed surveys - show if user has completed any or if there are no open surveys */}
      {closedSurveys.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-700">{t('event.survey.closedSurveysTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {closedSurveys.map((survey) => (
              <SurveyCard key={survey.id} survey={survey} />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming surveys */}
      {upcomingSurveys.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-yellow-700">{t('event.survey.upcomingSurveysTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcomingSurveys.map((survey) => (
              <SurveyCard key={survey.id} survey={survey} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
